// Shared layout for legal pages (Privacy, Terms, Security).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Mail, Calendar } from "lucide-react";
import { SITE } from "@/lib/site";
import { Container, Kicker, usePageTitle } from "@/components/ui-kit";

const TOC = ({ sections, activeId, slug }) => (
  <nav aria-label="On this page" className="sticky top-28 hidden lg:block" data-testid={`legal-${slug}-toc`}>
    <div className="eyebrow mb-3">On this page</div>
    <ul className="space-y-1 text-sm">
      {sections.map((s) => (
        <li key={s.id}>
          <a href={`#${s.id}`} data-testid={`legal-${slug}-toc-${s.id}`} className={`block px-3 py-1.5 rounded-md transition-colors ${activeId === s.id ? "bg-mk-lime/10 text-mk-lime font-medium" : "text-mk-dim hover:text-mk-text hover:bg-mk-raised/60"}`}>{s.title}</a>
        </li>
      ))}
    </ul>
  </nav>
);

const LegalPageLayout = ({ slug, eyebrow, title, lastUpdated, intro, sections, metaTitle }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  usePageTitle(metaTitle?.replace(" | Fixlr", ""));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: "-100px 0px -55% 0px", threshold: [0.1, 0.3, 0.6] }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div data-testid={`page-legal-${slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-50" aria-hidden />
        <Container className="relative pt-12 pb-10 lg:pt-16">
          <nav className="text-xs text-mk-muted flex items-center gap-1.5 mb-6" aria-label="Breadcrumb"><Link to="/" className="hover:text-mk-text">Home</Link><ChevronRight className="h-3 w-3" /><span className="text-mk-dim">{eyebrow}</span></nav>
          <Kicker>{eyebrow}</Kicker>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text max-w-3xl">{title}</h1>
          <div className="mt-5 flex items-center gap-2 text-sm text-mk-dim"><Calendar className="h-4 w-4" /><span>Last updated <span className="text-mk-text font-medium">{lastUpdated}</span></span></div>
          {intro && <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-3xl">{intro}</p>}
        </Container>
      </section>

      <section className="border-t border-mk-border bg-mk-surface/30">
        <Container className="py-16">
          <div className="grid lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-3"><TOC sections={sections} activeId={activeId} slug={slug} /></aside>
            <article className="lg:col-span-9 max-w-3xl">
              {sections.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-28 mb-12" data-testid={`legal-${slug}-section-${s.id}`}>
                  <h2 className="font-display text-2xl lg:text-3xl font-semibold text-mk-text tracking-tight"><span className="font-mono text-mk-lime mr-2 text-lg">{String(i + 1).padStart(2, "0")}.</span>{s.title}</h2>
                  <div className="mt-4 space-y-4 text-[15px] leading-relaxed prose-dark">{s.body}</div>
                </section>
              ))}
              <div className="mt-16 rounded-2xl border border-mk-lime/30 bg-mk-lime/[0.04] p-6">
                <h3 className="font-display text-lg font-semibold text-mk-text">Questions?</h3>
                <p className="mt-1 text-sm text-mk-dim">We're happy to walk you through any of this in plain English.</p>
                <a href={`mailto:${SITE.email}`} data-testid={`legal-${slug}-contact`} className="mt-3 inline-flex items-center gap-2 text-mk-lime font-medium text-sm"><Mail className="h-4 w-4" /> {SITE.email}</a>
              </div>
            </article>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default LegalPageLayout;
